/**
 * AccountCijfersSection — tabblad "Cijfers" op de accountpagina.
 *
 * Hetzelfde klantblok als in het Sales Dashboard (budget, gefactureerd,
 * opdrachten, open offertes + de lijsten), maar dan voor één account en met
 * klikbare projecten naar de projectpagina. Alle gekoppelde Gripp-bedrijven
 * tellen samen; dat doet de server (/Api/account/:id).
 *
 * Rechten: alleen met sales-rechten (sales_dashboard/view). De werkplek toont
 * het tabblad anders niet, en de API weigert zelf ook (403) — dit scherm is
 * dus niet de enige drempel.
 */
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, CircularProgress, LinearProgress, Typography } from '@mui/material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

const T = {
  primary: '#526075', surface: '#ffffff', surfaceLow: '#f1f4f5',
  text: '#2d3335', outline: '#767c7e', outlineVariant: '#adb3b5',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
};

export const eur = (v) => new Intl.NumberFormat('nl-NL', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** Werkbudget = reforecast als die er is, anders het originele budget (zoals het dashboard). */
export function voortgang(account) {
  const g = account?.grippData || {};
  const gerealiseerd = (g.invoices?.totalAmount || 0) + (g.projects?.totalAmount || 0);
  const budget = account?.reforecast2026 ?? account?.budget2026 ?? 0;
  const procent = budget ? Math.round((gerealiseerd / budget) * 100) : 0;
  return { gerealiseerd, budget, procent };
}

const kaartSx = {
  p: 2.5, borderRadius: '0.75rem', bgcolor: T.surface,
  border: '1px solid rgba(45,51,53,0.06)', minWidth: 0,
};

const Label = ({ children }) => (
  <Typography sx={{
    fontFamily: T.font, fontSize: '0.62rem', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.08em', color: T.outlineVariant,
  }}>
    {children}
  </Typography>
);

function Kengetal({ label, waarde, toelichting }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Label>{label}</Label>
      <Typography sx={{ fontFamily: T.font, fontSize: '1.35rem', fontWeight: 700, color: T.text, mt: 0.25 }}>
        {waarde}
      </Typography>
      {toelichting && (
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>
          {toelichting}
        </Typography>
      )}
    </Box>
  );
}

const regelSx = {
  display: 'flex', alignItems: 'center', gap: 1, py: 0.75,
  borderTop: '1px solid rgba(45,51,53,0.05)', '&:first-of-type': { borderTop: 'none' },
  color: T.text, textDecoration: 'none', minWidth: 0,
};

function Regel({ nummer, omschrijving, bedrag, extra, naar }) {
  const inhoud = (
    <>
      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', fontWeight: 700, flexShrink: 0 }}>
        {nummer}
      </Typography>
      <Typography noWrap title={omschrijving || ''}
        sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.outline, flex: 1, minWidth: 0 }}>
        {omschrijving || ''}
      </Typography>
      {extra && (
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, whiteSpace: 'nowrap' }}>
          {extra}
        </Typography>
      )}
      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
        {eur(bedrag)}
      </Typography>
    </>
  );
  if (naar) {
    return (
      <Box component={RouterLink} to={naar}
        sx={{ ...regelSx, borderRadius: '0.375rem', '&:hover': { bgcolor: T.surfaceLow } }}>
        {inhoud}
      </Box>
    );
  }
  // Geen projectpagina gevonden (of geen project): gewoon tekst, niet klikbaar.
  return <Box sx={regelSx}>{inhoud}</Box>;
}

function Lijst({ titel, totaal, leeg, children, aantal }) {
  return (
    <Box sx={kaartSx}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
        <Label>{titel} ({aantal})</Label>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.outline }}>
          {totaal}
        </Typography>
      </Box>
      {aantal === 0 ? (
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outlineVariant }}>{leeg}</Typography>
      ) : (
        <Box sx={{ maxHeight: 420, overflow: 'auto' }}>{children}</Box>
      )}
    </Box>
  );
}

export default function AccountCijfersSection({ accountId }) {
  const [account, setAccount] = useState(null);
  const [fout, setFout] = useState(null);
  const [laden, setLaden] = useState(true);

  useEffect(() => {
    let weg = false;
    setLaden(true);
    setFout(null);
    authFetch(`${BACKEND_URL}/Api/account/${accountId}`)
      .then(async (r) => {
        if (r.status === 403) throw new Error('Deze cijfers zijn alleen zichtbaar met sales-rechten.');
        if (!r.ok) throw new Error(`Cijfers laden mislukt (${r.status})`);
        return r.json();
      })
      .then((d) => { if (!weg) setAccount(d); })
      .catch((e) => { if (!weg) setFout(e.message); })
      .finally(() => { if (!weg) setLaden(false); });
    return () => { weg = true; };
  }, [accountId]);

  if (laden) return <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>;
  if (fout) return <Alert severity="info">{fout}</Alert>;
  if (!account) return null;

  const g = account.grippData || {};
  const offertes = g.offers?.items || [];
  const projecten = g.projects?.items || [];
  const facturen = g.invoices?.items || [];
  const { gerealiseerd, budget, procent } = voortgang(account);
  const heeftReforecast = account.reforecast2026 != null;
  const bedrijven = (account.grippIds || []).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {account.error && <Alert severity="warning">{account.error}</Alert>}
      {!bedrijven && !account.error && (
        <Alert severity="info">Dit account is nog niet aan een Gripp-bedrijf gekoppeld, dus er zijn geen cijfers.</Alert>
      )}

      {/* Kengetallen + voortgang */}
      <Box sx={kaartSx}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2.5 }}>
          <Kengetal label={heeftReforecast ? 'Budget 2026 (reforecast)' : 'Budget 2026'}
            waarde={eur(budget)}
            toelichting={heeftReforecast ? `origineel ${eur(account.budget2026)}` : null} />
          <Kengetal label="Gefactureerd" waarde={eur(g.invoices?.totalAmount)}
            toelichting={g.invoices?.openAmount > 0 ? `${eur(g.invoices.openAmount)} nog niet betaald` : 'alles betaald'} />
          <Kengetal label="Opdrachten" waarde={eur(g.projects?.totalAmount)}
            toelichting={`${g.projects?.count || 0} lopende projecten`} />
          <Kengetal label="Open offertes" waarde={eur(g.offers?.openTotal)}
            toelichting={g.offers?.weightedTotal != null && offertes.length
              ? `gewogen ${eur(g.offers.weightedTotal)}` : `${g.offers?.openCount || 0} open`} />
        </Box>
        <Box sx={{ mt: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outline }}>
              Gefactureerd + opdrachten: {eur(gerealiseerd)}
            </Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outline }}>
              {budget ? `${procent}% van budget` : 'geen budget'}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={Math.min(procent, 100)}
            sx={{ height: 6, borderRadius: 3, bgcolor: T.surfaceLow, '& .MuiLinearProgress-bar': { bgcolor: T.primary, borderRadius: 3 } }} />
        </Box>
        {bedrijven > 1 && (
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outlineVariant, mt: 1.5 }}>
            Opgeteld over {bedrijven} gekoppelde Gripp-bedrijven.
          </Typography>
        )}
      </Box>

      {/* Lijsten: offertes · lopende projecten (klikbaar) · facturen */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
        <Lijst titel="Open offertes" aantal={offertes.length} totaal={eur(g.offers?.openTotal)} leeg="Geen open offertes">
          {offertes.map((o) => (
            <Regel key={o.id} nummer={o.number} omschrijving={o.subject} bedrag={o.totalAmount} extra={o.phaseName} />
          ))}
        </Lijst>

        <Lijst titel="Lopende projecten" aantal={projecten.length} totaal={eur(g.projects?.totalAmount)} leeg="Geen lopende projecten">
          {projecten.map((p) => (
            <Regel key={p.id} nummer={p.number} omschrijving={p.name} bedrag={p.totalAmount}
              extra={p.statusName} naar={p.projectId ? `/projects/${p.projectId}` : null} />
          ))}
        </Lijst>

        <Lijst titel="Facturen" aantal={facturen.length} totaal={eur(g.invoices?.totalAmount)} leeg="Geen facturen">
          {facturen.map((f) => (
            <Regel key={f.id} nummer={f.number} omschrijving={f.subject} bedrag={f.totalAmount}
              extra={f.isPaid ? (f.date || '').slice(0, 10) : 'open'} />
          ))}
        </Lijst>
      </Box>
    </Box>
  );
}
